/**
 * The hidden signal (Marek, 2026-09-30): in 2213 the drinks come on coasters with an NFC sticker
 * underneath. A guest who thinks to hold their phone to one opens its link — the guest app, with
 * `?signal=<code>` — and a secret neon billboard from the city plays on their phone. During 2213 it is
 * also worth points, once a guest an evening; in any other era it plays and gives nothing.
 *
 * Where it is stays secret (Marek): no screen ever says which coaster, or that it is a coaster at all — the
 * coaster's code stays in the room. Each find is announced on the projector by call sign (Marek, 2026-10-15:
 * "visible on the main projector screen as a pop-up window when someone finds the hidden signal, without
 * revealing where it was"), so the finds go on the wire as call sign and time only (`SignalShown`, wire.ts); a
 * guest's own phone learns its own through `You.signal`. The points show on the leaderboard like any others.
 */

export const SIGNAL = {
  /** The era the coasters are on the table. */
  chapter: 2,
  /** As much as a whole game at par (Marek). */
  points: 1000,
} as const;

/**
 * One code a coaster, written into its sticker's link (`signalLink`). Short, so the link fits any
 * sticker, and not guessable from one another.
 */
export const SIGNAL_CODES: readonly string[] = [
  'K7Q2', 'M3X9', 'R8T4', 'V2N6', 'H5W1', 'P9C3',
  'D4J8', 'Z6F2', 'B1L7', 'T3Y5', 'G8S1', 'X5K9',
];

export function isSignalCode(code: unknown): code is string {
  return typeof code === 'string' && SIGNAL_CODES.includes(code.toUpperCase());
}

/** The link a coaster's sticker holds. */
export function signalLink(code: string, origin = 'https://app.ss-aurora.no'): string {
  return `${origin}/?signal=${code}`;
}

/** What the room keeps of a guest's find: which coaster, and when. */
export interface SignalFind {
  code: string;
  at: number;
}

/** A find as every screen may see it: who and when, never which coaster or where they sit. */
export interface SignalShown {
  callsign: string;
  at: number;
}
